import moment from "moment";
import "moment/locale/gu";

import { rateNumber } from "./processRate";
import {
  IAdditionalCostInput,
  IMaterialLineInput,
  LineHintProps,
  PartyInfoProps,
} from "../types/materialLine";

// Loading a moment locale also makes it the global one; the app formats its dates in English.
moment.locale("en");

// Helpers shared by material orders and material purchases.

export const lineKey = (materialId: number, sizeId: number) =>
  `${materialId}:${sizeId}`;

export const orderLineKey = (orderItemId: number) => `o:${orderItemId}`;

// A purchase line is one per order line it receives, and one per material size without an order line.
export const purchaseLineKey = (line: IMaterialLineInput) =>
  line.order_item_id > 0
    ? orderLineKey(line.order_item_id)
    : lineKey(line.material_id, line.material_size_id);

export const emptyLine: IMaterialLineInput = {
  material_id: 0,
  material_size_id: 0,
  order_item_id: 0,
  order_id: 0,
  close_order: false,
  qty: "",
  price: "",
};

// A qty box keeps whole numbers only.
export const sanitizeQty = (text: string) =>
  text.replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, 9);

export const isWholeQty = (text: string | null | undefined) =>
  /^\d+$/.test(text ?? "") && Number(text) > 0;

export const lineAmount = (line: { qty: string; price: string }) =>
  Math.round((Number(line.qty) || 0) * (rateNumber(line.price) || 0) * 100) /
  100;

export const filledLines = (lines: IMaterialLineInput[]) =>
  (lines || []).filter((line) => line.material_id > 0);

export const sameLines = (a: IMaterialLineInput[], b: IMaterialLineInput[]) =>
  a.length === b.length &&
  a.every(
    (line, index) =>
      line.material_id === b[index].material_id &&
      line.material_size_id === b[index].material_size_id &&
      line.order_item_id === b[index].order_item_id &&
      line.close_order === b[index].close_order &&
      line.qty === b[index].qty &&
      line.price === b[index].price
  );

// The lines as the API takes them. Only a purchase line has a price, and only from a user who may enter rates
// (withPrice); a purchase line that receives an order line says which, and whether it closes it.
export const linePayload = (lines: IMaterialLineInput[], withPrice: boolean) =>
  filledLines(lines).map((line) => ({
    material_id: line.material_id,
    material_size_id: line.material_size_id,
    qty: Number(line.qty),
    ...(line.order_item_id > 0
      ? { order_item_id: line.order_item_id, close_order: line.close_order }
      : {}),
    ...(withPrice ? { price: rateNumber(line.price) ?? 0 } : {}),
  }));

// A purchase's additional costs added up, as the form's total counts them.
export const costsAmount = (costs: IAdditionalCostInput[] | undefined) =>
  Math.round(
    (costs || []).reduce(
      (sum, cost) => sum + (rateNumber(cost?.amount) || 0),
      0
    ) * 100
  ) / 100;

// A purchase's additional costs as the API takes them; only a user who may enter rates sends them.
export const costPayload = (costs: IAdditionalCostInput[] | undefined) =>
  (costs || []).map((cost) => ({
    note: cost.note.trim(),
    amount: rateNumber(cost.amount) ?? 0,
  }));

// The lines without their order lines, once the party (and so its orders) changed. Lines of the same material size
// then become one: their qty is added up and the first one's price kept.
export const unlinkLines = (lines: IMaterialLineInput[]) =>
  lines.reduce<IMaterialLineInput[]>((result, line) => {
    const plain = { ...line, order_item_id: 0, order_id: 0, close_order: false };
    const same =
      plain.material_id > 0 &&
      result.find(
        (other) =>
          other.material_id === plain.material_id &&
          other.material_size_id === plain.material_size_id
      );

    if (!same) return [...result, plain];

    const qty = (Number(same.qty) || 0) + (Number(plain.qty) || 0);

    return result.map((other) =>
      other === same ? { ...same, qty: qty > 0 ? String(qty) : "" } : other
    );
  }, []);

// A saved price as the price box's text, e.g. 50.5 -> "50.5".
export const priceText = (value: number | null | undefined) =>
  value === null || value === undefined ? "" : String(Number(value));

export const formatAmount = (value: number | string | null | undefined) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const formatQty = (value: number | string | null | undefined) =>
  Number(value || 0).toLocaleString("en-IN");

export const formatDateTime = (value: string) =>
  moment(value).format("DD-MM-YYYY hh:mm A");

// "૧૭/૧૦/૨૫ ૦૮:૨૦ રાત": how the purchase picker's order cards date their order, like the user's reference app.
export const formatGujaratiDateTime = (value: string) =>
  moment(value).locale("gu").format("DD/MM/YY hh:mm A");

// "૧૧ ઑગસ્ટ, ૨૦૨૬ ૦૭:૪૬ સાંજ": how the order and purchase detail pages date things, like the user's reference app.
export const formatGujaratiLongDateTime = (value: string) =>
  moment(value).locale("gu").format("DD MMMM, YYYY hh:mm A");

export const partyText = (party: {
  firm_name?: string | null;
  name?: string | null;
  phone?: string | null;
}) => {
  const parts = [party.firm_name, party.name].filter(Boolean).join(" - ");

  if (!parts && !party.phone) return "-";

  return party.phone ? `${parts} (${party.phone})` : parts;
};

export type OrderStateProps = {
  value: "open" | "close";
  label: string;
  badgeClass: string;
};

// An order is Open while anything is still to come (stored status 0 pending or 1 partial) and Close once all of it is
// received (2). Keep in step with MaterialOrder::STATES on the backend.
export const orderStates: OrderStateProps[] = [
  {
    value: "open",
    label: "Open",
    badgeClass: "bg-orange-100 text-orange-800 hover:bg-orange-100",
  },
  {
    value: "close",
    label: "Close",
    badgeClass: "bg-green-100 text-green-800 hover:bg-green-100",
  },
];

export const isOpenOrder = (status: number) => Number(status) !== 2;

export const orderState = (status: number) =>
  orderStates[isOpenOrder(status) ? 0 : 1];

export type PurchaseStateProps = {
  value: "pending" | "complete";
  label: string;
  badgeClass: string;
};

// A purchase is Pending while any line waits for its rate (saved by a user who can't see rates) and Complete once all
// have one. Only users who may see rates see it. Keep in step with MaterialPurchase::PENDING/COMPLETE on the backend.
export const purchaseStates: PurchaseStateProps[] = [
  {
    value: "pending",
    label: "Pending",
    badgeClass: "bg-orange-100 text-orange-800 hover:bg-orange-100",
  },
  {
    value: "complete",
    label: "Complete",
    badgeClass: "bg-green-100 text-green-800 hover:bg-green-100",
  },
];

export const purchaseState = (status: number) =>
  purchaseStates[Number(status) === 0 ? 0 : 1];

// What the form shows with each line, by line key: the party's last price and the qty pending on its open orders (with
// what their lines still awaited ordered and received), and on an order being edited the qty received of each line and
// the lines a purchase closed.
export const lineHints = (
  partyInfo: PartyInfoProps[],
  received?: Record<string, number>,
  closed?: string[]
): Record<string, LineHintProps> => {
  const hints: Record<string, LineHintProps> = {};

  const hintOf = (key: string) =>
    hints[key] ||
    (hints[key] = {
      lastPrice: null,
      pending: null,
      openOrdered: 0,
      openReceived: 0,
      received: 0,
      closed: false,
    });

  partyInfo.forEach((info) => {
    const hint = hintOf(lineKey(info.material_id, info.material_size_id));

    hint.lastPrice = info.last_price;
    hint.pending = info.pending_qty > 0 ? info.pending_qty : null;
    hint.openOrdered = Number(info.ordered_qty) || 0;
    hint.openReceived = Number(info.received_qty) || 0;
  });

  Object.entries(received || {}).forEach(([key, qty]) => {
    hintOf(key).received = qty;
  });

  (closed || []).forEach((key) => {
    hintOf(key).closed = true;
  });

  return hints;
};
